/**
 * AF-234 — pricing-history content SOT for the public `/pricing/history` route.
 *
 * Structured from `docs/pricing/pricing-history.md` (the canonical Markdown
 * source). The page is the record behind `<domain>/pricing`: every price change,
 * dated, with the reason and who keeps their old rate. The bootstrap entry
 * resolves to the launch date on the first paying customer (a legitimate
 * pre-launch state, not a TODO; see the source doc).
 *
 * There is no `signedKey`. Each entry used to carry one, rendering "Signed off
 * by the CEO" as a field on a customer-facing page — a record of OUR internal
 * governance, on a page a buyer opens to answer "will you put my price up?".
 * The commitment is what they came for; who countersigned it here is not
 * evidence they can use.
 */

/** A one-way commitment anchoring the pricing-history page. */
export interface PricingCommitment {
  /** Stable id (for keys / attributes) — never user-visible. */
  readonly id: string;
  /** i18n key for the commitment title. */
  readonly titleKey: string;
  /** i18n key for the commitment detail. */
  readonly detailKey: string;
}

export const PRICING_COMMITMENTS: readonly PricingCommitment[] = [
  {
    id: 'predictable',
    titleKey: 'pricing.history.commitment.predictable.title',
    detailKey: 'pricing.history.commitment.predictable.detail',
  },
  {
    id: 'grandfathering',
    titleKey: 'pricing.history.commitment.grandfathering.title',
    detailKey: 'pricing.history.commitment.grandfathering.detail',
  },
];

export interface GrandfatheringRule {
  /** Stable id used for the `data-rule` attribute — never user-visible. */
  readonly id: string;
  /** i18n key for the rule kind (heading). */
  readonly kindKey: string;
  /** i18n key for the rule body. */
  readonly ruleKey: string;
}

export const GRANDFATHERING_RULES: readonly GrandfatheringRule[] = [
  {
    id: 'price-increases',
    kindKey: 'pricing.history.grandfathering.priceIncreases.kind',
    ruleKey: 'pricing.history.grandfathering.priceIncreases.rule',
  },
  {
    id: 'price-decreases',
    kindKey: 'pricing.history.grandfathering.priceDecreases.kind',
    ruleKey: 'pricing.history.grandfathering.priceDecreases.rule',
  },
  {
    id: 'tier-change-up',
    kindKey: 'pricing.history.grandfathering.tierChangeUp.kind',
    ruleKey: 'pricing.history.grandfathering.tierChangeUp.rule',
  },
  {
    id: 'tier-change-down',
    kindKey: 'pricing.history.grandfathering.tierChangeDown.kind',
    ruleKey: 'pricing.history.grandfathering.tierChangeDown.rule',
  },
  {
    id: 'new-feature-add-ons',
    kindKey: 'pricing.history.grandfathering.addOns.kind',
    ruleKey: 'pricing.history.grandfathering.addOns.rule',
  },
];

export interface PricingChangeEntry {
  /** ISO date, or `DATE_TBD` for the pre-launch bootstrap entry (resolves on launch). */
  readonly date: string;
  /** i18n key for the human date label when not yet a real ISO date. */
  readonly dateLabelKey: string;
  readonly summaryKey: string;
  readonly changeKey: string;
  readonly priorPricingKey: string;
  /** i18n keys for the per-line new-pricing bullets. */
  readonly newPricingKeys: readonly string[];
  readonly affectedKey: string;
  readonly grandfatheredKey: string;
  readonly whyKey: string;
}

/**
 * The placeholder date the bootstrap entry carries until the first paid
 * subscription resolves it. Named so the ordering rule below can refer to it
 * rather than repeating the literal.
 */
export const UNDATED_ENTRY = 'DATE_TBD';

export const PRICING_CHANGE_ENTRIES: readonly PricingChangeEntry[] = [
  {
    // ADR-0776 — the posted menu re-set against MEASURED cost (6 / 15 / 18 / 30)
    // and turned from an additive ladder into a four-cell table. Tier prices and
    // included allowances are again unchanged; what each interaction draws from
    // the allowance is not, which is why this is its own entry rather than an
    // edit to the one below. The history is append-only: the 2026-08-04 entry
    // records what was in force then, and rewriting it would falsify the
    // grandfathering record the MSA points customers at.
    date: '2026-09-10',
    dateLabelKey: 'pricing.history.entry.postedRebalance.dateLabel',
    summaryKey: 'pricing.history.entry.postedRebalance.summary',
    changeKey: 'pricing.history.entry.postedRebalance.change',
    priorPricingKey: 'pricing.history.entry.postedRebalance.priorPricing',
    newPricingKeys: [
      'pricing.history.entry.postedRebalance.newPricing.answer',
      'pricing.history.entry.postedRebalance.newPricing.acted',
      'pricing.history.entry.postedRebalance.newPricing.lookedUp',
      'pricing.history.entry.postedRebalance.newPricing.both',
      'pricing.history.entry.postedRebalance.newPricing.tiers',
    ],
    affectedKey: 'pricing.history.entry.postedRebalance.affected',
    grandfatheredKey: 'pricing.history.entry.postedRebalance.grandfathered',
    whyKey: 'pricing.history.entry.postedRebalance.why',
  },
  {
    // ADR-0442 — the posted-price interaction menu. Tier prices and included
    // allowances are UNCHANGED; what moved is how an interaction is priced.
    // Superseded on 2026-09-10 by the entry above; kept verbatim as the record
    // of what was in force between the two dates.
    date: '2026-08-04',
    dateLabelKey: 'pricing.history.entry.postedPrice.dateLabel',
    summaryKey: 'pricing.history.entry.postedPrice.summary',
    changeKey: 'pricing.history.entry.postedPrice.change',
    priorPricingKey: 'pricing.history.entry.postedPrice.priorPricing',
    newPricingKeys: [
      'pricing.history.entry.postedPrice.newPricing.answer',
      'pricing.history.entry.postedPrice.newPricing.acted',
      'pricing.history.entry.postedPrice.newPricing.lookedUp',
      'pricing.history.entry.postedPrice.newPricing.both',
      'pricing.history.entry.postedPrice.newPricing.tiers',
    ],
    affectedKey: 'pricing.history.entry.postedPrice.affected',
    grandfatheredKey: 'pricing.history.entry.postedPrice.grandfathered',
    whyKey: 'pricing.history.entry.postedPrice.why',
  },
  {
    date: UNDATED_ENTRY,
    dateLabelKey: 'pricing.history.entry.initial.dateLabel',
    summaryKey: 'pricing.history.entry.initial.summary',
    changeKey: 'pricing.history.entry.initial.change',
    priorPricingKey: 'pricing.history.entry.initial.priorPricing',
    newPricingKeys: [
      'pricing.history.entry.initial.newPricing.starter',
      'pricing.history.entry.initial.newPricing.growth',
      'pricing.history.entry.initial.newPricing.scale',
      'pricing.history.entry.initial.newPricing.enterprise',
    ],
    affectedKey: 'pricing.history.entry.initial.affected',
    grandfatheredKey: 'pricing.history.entry.initial.grandfathered',
    whyKey: 'pricing.history.entry.initial.why',
  },
];

/**
 * Change entries newest-first, which is how the page presents them.
 *
 * `UNDATED_ENTRY` sorts LAST, not first. A plain descending `localeCompare`
 * puts it on top because 'D' > any digit — harmless while it was the only
 * entry, and wrong the moment a second one lands: the bootstrap entry documents
 * the ORIGINAL pricing, so it belongs at the bottom of an append-only history.
 * A pricing page whose whole purpose is to be the audit trail is the one place
 * a reader is entitled to assume the order IS the record.
 */
export function pricingChangeEntriesNewestFirst(): readonly PricingChangeEntry[] {
  const rank = (entry: PricingChangeEntry): string =>
    entry.date === UNDATED_ENTRY ? '' : entry.date;
  return [...PRICING_CHANGE_ENTRIES].sort((a, b) => rank(b).localeCompare(rank(a)));
}
